import { Key } from "../../../../types/skyward/svg";
import { Dictionary } from "../../../../types/util/dictionary";
import { DataSource } from "../../core/store/features/datasource";
import { SkyVueData, State } from "../../core/store/types";
import { GroupByField } from "../components/chart/types";
import { dashboardItemStoreFeatures } from "./features";
import { DashboardItemSettings } from "./features/settings";

export type DashboardItemStoreFeatures = typeof dashboardItemStoreFeatures;

export type DashboardItemStoreState<TFeatureTypes, TDataType extends object = Dictionary<string, any>> = State<DashboardItemStoreFeatures, DashboardItemSetup, TFeatureTypes, TDataType>;

export type DashboardItemRecord =
    {
        readonly DashboardItemID: number;
        readonly DashboardItem: Omit<SkyVueData, 'features'> & { features: DashboardItemFeatures };
    }

export type DashboardItemSetup =
{
    /** `dashboardItem_${rawDashboardItemID}` */
    readonly dashboardItemID: string;
    /** Literal ID value, as a string */
    readonly rawDashboardItemID: string;
    readonly hasBackingScreen: boolean;
    onDownloadImage: (callback: () => void) => void;
    loadedMedia: { [key: string | number]: boolean };
    isSelected: boolean;
    setMessageBusy: (isBusy: boolean) => PromiseLike<any>;
    showSuccessMessage: (messageText: string) => PromiseLike<any>;
    showErrorMessage: (messageText: string) => PromiseLike<any>;
    tryDownloadImage: () => void;
    refresh(): void;
};

export interface DashboardItemFeatures
{
    dataSource: DataSource & DashboardItemDataSource;
    settings: DashboardItemSettings;
}

interface LinkTileProps
{
    svgName: Key,
    postData: Record<string, unknown>,
}

interface LiveTileProps
{
    svgName: Key,
}

export type TileProps = Record<string, unknown> & (LinkTileProps | LiveTileProps);

export interface TileFeatures<TParameters>
{
    dataSource: DataSource & DashboardItemDataSource;
    props: TileProps;
    settings:
    {
        parameters: TParameters;
    }
}

export interface WellnessScreeningTileFeatures
{
    props: WellnessScreeningTileProps;
    settings:
    {
        parameters: EmptyParameters;
    }
}

interface WellnessScreeningTileProps
{
    postData: Record<string, unknown>;
    displayName: string;
    nameID: number;
    statusText: string;
    statusDate?: string;
    statusClassName: string;
}

export type EmptyParameters = {};

export type ParamaterType = { [key: string]: unknown } & (WidgetParameters | CardWidgetParameters | ApprovalWidgetParameters | ChartWidgetParameters | TileParameters | LiveTileParameters | SectionTileParameters | ChartTileParameters);

export enum DashboardItemTypes
{
    NotSet = "NS",
    Link = "LK",
    Live = "LV",
    Chart = "CH",
    Section = "SE",
    Widget = "WI",
};

export enum ColorOptions
{
    Theme = "Theme",
    Custom = "Custom"
};

// Keep in sync with WidgetTypes in WidgetParameters.cs
export enum WidgetTypes 
{
    LoginHistory,
    RecentlyVisited,
    TimeOffApproval,
    TimesheetApproval,
    MyMessages,
    DisciplineIncident,
    DisciplineAction,
    StudentEnrollment,
    Attendance,
    YearOverYearExpenditures,
    StudentPortalEngagement,
    FamilyPortalEngagement,
    ActivityVsBudgeted
};

export type TileParameters =
{
    area: string;
    controller: string;
    screen: string;
    entityID: number;
    districtID: number;
    schoolYearID: number;
    fiscalYearID: number;
    iconColor: string;
    backgroundColor: string;
    textColor: string;
    renderIcon: boolean;
    colorOption: ColorOptions;
};

export type SectionTileParameters = TileParameters &
{
    sectionID: number;
    classGroupID: number;
};

export type LiveTileParameters = TileParameters &
{
    browseModule: string;
    browseObject: string;
    browseName: string;
    browseViewID?: number;
    browseViewIDClonedFrom?: number;
    browseFilterID?: number;
    browseFilterIDClonedFrom?: number;
};

// Keep in sync with ChartTypes in ChartTileParameters.cs
export enum ChartTypes
{
    Bar = "Bar",
    Pie = "Pie",
    Column = "Column",
    Line = "Line",
};

// Keep in sync with AggregationTypes in ChartTileParameters.cs
export enum AggregationTypes
{
    COUNT = "COUNT",
    SUM = "SUM",
    AVG = "AVG",
    DISPLAY = "DISPLAY",
};

// Keep in sync with LabelDisplayTypes in ChartTileParameters.cs
export enum LabelDisplayTypes
{
    Auto = "Auto",
    None = "None",
    Rotate = "Rotate",
    Slant = "Slant",
    Stagger = "Stagger",
    Vertical = "Vertical",
    Wrap = "Wrap",
};

export enum PieChartValueFormats
{
    Count = "Count",
    Percentage = "Percentage"
};

export enum PieChartAnimationType
{
    AnimateClockwise = "AnimateClockwise",
    None = "None"
};

export type ChartTileParameters = LiveTileParameters &
{
    chartTitle: string;
    chartType: ChartTypes;
    aggregationType: AggregationTypes;
    xAxisVariable: string;
    xAxisLabel: string;
    yAxisLabel: string;
    yAxisVariable: string;
    axisLabelColor: string;
    showAlternateHGridColor: boolean;
    showAlternateVGridColor: boolean;
    showLabels: boolean;
    numberPrefix: string;
    numberSuffix: string;
    useRoundEdges: boolean;
    plotHoverEffect: boolean;
    labelDisplayType: LabelDisplayTypes;
    pieChartDisplay: PieChartValueFormats;
    rotateValues: boolean;
    showValues: boolean;
    showCanvasBorder: boolean;
    bgColor: string;
    bgAlpha: string;
    canvasBgColor: string;
    canvasBgAlpha: string;
    borderColor: string;
    borderAlpha: string;
    captionFontColor: string;
    slantLabels: boolean;
    staggerLines: string;
    palette: string;
    paletteColors: string;
    animateClockwise: boolean;
    animate: boolean;
    showPercentValues: boolean;
    isMulticolor: boolean;
    pieChartAnimation: PieChartAnimationType;
};

export type WidgetParameters =
    {
        widgetTypes: WidgetTypes
    };

export type CardWidgetParameters = WidgetParameters &
{
    displayCardPhotos: boolean;
};

export type ApprovalWidgetParameters = CardWidgetParameters &
{
    sortTypesList: string[];
    sortType: string;
    displayEmployeeNumber: boolean;
    displayMassApproveDenyButton: boolean;
};

export type ChartWidgetParameters = WidgetParameters &
{
    prefix: string;
    suffix: string;
    showHoverEffect: boolean;
    showColumnValues: boolean;
    labelDisplayType: string;
    selectedGroupByFields: string[];
    browseFilterID?: number;
    browseFilterIDsByBrowse: { [key: string]: number };
    lastUsedBrowseFilterID: number;
    drillDownValue: string | null;
    windowEntityOverride: number | string | null;
    windowSchoolYearOverride: number | string | null;
    windowFiscalYearOverride: number | string | null;
};

export enum MenuOptions
{
    Refresh,
    Settings,
    Clone,
    Download,
    Delete
};

type PreviewDataSource =
{
    isPreview: boolean
};

type DashboardItemDataSource = PreviewDataSource &
{
    dashboardItemID: number
};

type ApprovalsDataSource = DashboardItemDataSource &
{
    sortType: string;
};

type ApprovalsSettings =
{
    settings:
    {
        parameters: ApprovalWidgetParameters
    }
};

export type ApprovalsFeatures = CardListViewModel<ApprovalsDataSource> & ApprovalsSettings;

export type CardViewModel =
{
    expanded: boolean;
};

type CardSettings =
{
    settings:
    {
        parameters: CardWidgetParameters
    }
};

export type CardListFeatures = CardListViewModel<DashboardItemDataSource> & CardSettings

type CardListViewModel<TDataSource> =
{
    viewModel:
    {
        cards: CardViewModel[];
        expandable: boolean;
        isEditing: boolean;
        isSmall: boolean;
    },
    dataSource: DataSource & TDataSource;
};

export type ChartWidgetFeatures =
{
    props:
    {
        availableGroupByFields: GroupByField[];
    },
    settings:
    {
        parameters: ChartWidgetParameters;
    }
};